import {Component, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {AuditHistoryApiService, EntityType, HistoryEntry, HistoryEventType} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, NESTED_TAB, parseUUID, rxSpinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {CONTACT_DETAILS_ID, INCIDENT_ID, REFUND_ID, SUBJECT_ID, TRANSACTION_ID} from '../../domain/query-params.keys';
import {Path} from '../../path';
import {fieldExtractor, FilterEvent, FilterField, FilterSelectField, getFormattedDateOrNull} from '../full-table/filter/filter.component';
import {initialPageEvent, mergeTableEvent, TableEvent} from '../full-table/full-table.component';
import {PageEvent} from '../full-table/paging/paginator.component';
import {Column, ColumnType, CustomLayoutColumn, CustomLayoutItem, Row} from '../full-table/table/table.component';
import {ChangeDetailDialogComponent, ChangeDetailDialogData} from './change-detail-dialog/change-detail-dialog.component';

const RESOLUTIONS_TAB = 'tabResolutions';

@Component({
    selector: 'af-entity-history',
    templateUrl: 'entity-history.component.html',
    standalone: false,
})
export class EntityHistoryComponent implements OnInit {
    @Input({required: true}) entityId!: string;
    @Input({required: true}) entityType!: EntityType;
    @Input() mode: 'single' | 'full' = 'single';

    filterFields: FilterField[] = [
        {name: 'query', type: 'text'},
        {name: 'separatedDateRange', type: 'dateRange', translationPrefix: 'history'},
        <FilterSelectField<HistoryEventType>>{
            name: 'eventType',
            type: 'select',
            isMultiSelect: false,
            values: Object.values(HistoryEventType),
        },
    ];
    dataSource: Row[] = [];
    totalElements = 0;
    pageEvent: PageEvent = initialPageEvent;
    filterEvent: FilterEvent;

    private tableEvent: TableEvent = {page: this.pageEvent, spinnerId: 'initial'};

    constructor(
        private historyApi: AuditHistoryApiService,
        private dialogService: DialogService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.loadPage(this.tableEvent);
    }

    loadPage(tableEvent: TableEvent): void {
        this.tableEvent = mergeTableEvent(this.tableEvent, tableEvent);
        this.pageEvent = this.tableEvent.page!;
        this.filterEvent = this.tableEvent.filter;
        const filter = this.tableEvent.filter ?? {};
        const recordId = filter.query ? parseUUID(filter.query) : null;
        if (filter.query && !recordId) {
            this.dataSource = [];
            this.totalElements = 0;
            return;
        }
        this.historyApi.getEntityHistory(this.entityId, {
            includeChildren: this.mode === 'full',
            entityType: this.entityType,
            recordId: recordId ?? undefined,
            eventType: fieldExtractor(filter.eventType)?.[0] as HistoryEventType,
            from: getFormattedDateOrNull(filter.separatedDateRange?.startDate?.date, DATE_TIME_SERVER_FORMAT),
            to: getFormattedDateOrNull(filter.separatedDateRange?.endDate?.date, DATE_TIME_SERVER_FORMAT),
            sortOrder: this.tableEvent.sortBy?.direction,
            pageSize: this.pageEvent.pageSize,
            offset: this.pageEvent.pageOffset,
        }).pipe(rxSpinner('history')).subscribe(response => {
            this.dataSource = response.history.map(entry => this.toRow(entry));
            this.totalElements = response.total;
        });
    }

    private toRow(entry: HistoryEntry): Row {
        return {
            date: {type: ColumnType.DATE_TIME_SECONDS, text: entry.datetime, sortable: true},
            action: this.buildActionCell(entry),
            user: {type: ColumnType.TEXT, text: entry.author ?? '-'},
            addedBy: {
                type: ColumnType.TEXT,
                text: entry.userType ? `i18n_af_incidentHistoryAddedBy#${entry.userType}` : '-',
            },
            actionButton: <CustomLayoutColumn>{
                type: ColumnType.CUSTOM_LAYOUT,
                cellStyle: {
                    layoutDirection: 'row',
                    layoutAlignFirst: 'end',
                    layoutAlignSecond: 'center',
                    layoutGap: '8px',
                },
                layoutContentItems: this.buildIcons(entry),
            },
        };
    }

    private buildActionCell(entry: HistoryEntry): Column {
        const key = `i18n_af_history_${entry.entityType}_${entry.eventType.toLowerCase()}`;
        return {type: ColumnType.TEXT, text: this.translate.instant(key)};
    }

    private buildIcons(entry: HistoryEntry): CustomLayoutItem[] {
        const hasContent = entry.eventType === HistoryEventType.UPDATED
            ? entry.changes.length > 0
            : !!entry.fullSnapshot;
        if (!hasContent) {
            return [];
        }
        return [{
            type: 'text',
            text: 'i18n_af_history_showDetails',
            click: () => this.openDetailDialog(entry),
        }];
    }

    private openDetailDialog(entry: HistoryEntry): void {
        this.dialogService.openCustomDialogFitContent(
            ChangeDetailDialogComponent,
            <ChangeDetailDialogData>{entry, navigationTarget: this.resolveNavigation(entry)},
            {autoFocus: false},
        );
    }

    private resolveNavigation(entry: HistoryEntry): NavigationTarget | null {
        if (entry.entityType === this.entityType && entry.entityId === this.entityId) {
            return null;
        }
        switch (entry.entityType) {
            case EntityType.REFUND:
                return {path: Path.REFUND_DETAILS, pathParams: {[REFUND_ID]: entry.entityId}};
            case EntityType.SUBJECT:
                return {path: Path.SUBJECT_DETAILS, pathParams: {[SUBJECT_ID]: entry.entityId}};
            case EntityType.RESOLUTION:
                return this.entityType === EntityType.INCIDENT
                    ? {
                        path: Path.INCIDENT_DETAILS,
                        pathParams: {[INCIDENT_ID]: this.entityId},
                        queryParams: {[NESTED_TAB]: RESOLUTIONS_TAB},
                    }
                    : null;
            case EntityType.CONTACT_DETAILS:
                return this.entityType === EntityType.INCIDENT
                    ? {
                        path: Path.INCIDENTS_CONTACT_DETAILS_DETAILS,
                        pathParams: {[INCIDENT_ID]: this.entityId, [CONTACT_DETAILS_ID]: entry.entityId},
                    }
                    : null;
            case EntityType.TRANSACTION:
                return this.resolveTransactionNavigation(entry);
            default:
                return null;
        }
    }

    private resolveTransactionNavigation(entry: HistoryEntry): NavigationTarget | null {
        switch (this.entityType) {
            case EntityType.REFUND:
                return {
                    path: Path.REFUNDS_TRANSACTIONS_DETAILS,
                    pathParams: {[REFUND_ID]: this.entityId, [TRANSACTION_ID]: entry.entityId},
                };
            case EntityType.INCIDENT:
                return {
                    path: Path.INCIDENTS_TRANSACTIONS_DETAILS,
                    pathParams: {[INCIDENT_ID]: this.entityId, [TRANSACTION_ID]: entry.entityId},
                };
            default:
                return null;
        }
    }
}

export interface NavigationTarget {
    path: string;
    pathParams?: Record<string, string>;
    queryParams?: Record<string, string>;
}
